/**
 * The template library: every template utuwrk ships, from a short onboarding
 * for a small team to ones for healthcare (HIPAA) and defense contractors
 * (CUI and ITAR).
 *
 * The four starters (starters.ts) are offered when work is started, as they
 * always were. The rest wait in the library until an admin uses one: using
 * it makes the organisation's own copy, which takes the place of the starter
 * it is a variant of (its `basedOn`) at the sites it is offered at, so a
 * clinic that adopts the healthcare onboarding is not also offered the plain
 * one by mistake (decisions.md, "A library of templates").
 *
 * Written from the rules themselves -- USCIS, HHS, NIST SP 800-171, the ITAR,
 * the Department of Labor -- and generic on purpose: no organisation's forms,
 * products or vendors. Each regulated card says what it is for and where the
 * rule comes from, as guidance and not legal advice.
 */

import { COBRA_DESCRIPTION, I9_DESCRIPTION, hard, notFor, soft, yes } from "./build.js";
import {
  ACCESS_CHANGE_TEMPLATE,
  INVOLUNTARY_OFFBOARDING,
  STANDARD_OFFBOARDING,
  STANDARD_ONBOARDING,
} from "./starters.js";
import type { Template } from "./template.js";

/* --------------------------------------------------------------- simple -- */

/** The least a hire needs: for a team of a few people, with no Facilities or Safety to speak of. */
export const SIMPLE_ONBOARDING: Template = {
  id: "library-simple-onboarding",
  name: "Simple onboarding",
  version: 1,
  kind: "onboarding",
  basedOn: "starter-onboarding",
  nodes: [
    {
      key: "offer",
      kind: "task",
      title: "Offer accepted and start date confirmed",
      department: "HR",
      deadline: { from: "anchor", days: -7 },
    },
    {
      key: "accounts",
      kind: "task",
      title: "Email and accounts set up",
      department: "IT",
      deadline: { from: "anchor", days: -2 },
    },
    {
      key: "equipment",
      kind: "task",
      title: "Laptop and equipment ready",
      department: "IT",
      deadline: { from: "anchor", days: -1 },
      fields: [{ key: "device-serial", label: "Laptop serial number", issues: "device" }],
    },
    {
      key: "first-day-plan",
      kind: "task",
      title: "First-day plan ready",
      description: "Who meets them, where they sit, and what they do first.",
      department: "Management",
      deadline: { from: "anchor", days: -1 },
    },
    {
      key: "payroll",
      kind: "task",
      title: "Payroll and tax forms done",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      subtasks: ["W-4 (federal and state withholding)", "Direct deposit", "Emergency contact"],
    },
    {
      key: "i9",
      kind: "task",
      title: "I-9 verification completed",
      description: I9_DESCRIPTION,
      department: "HR",
      deadline: { from: "anchor", days: 3, unit: "business" },
      compliance: true,
      fields: [{ key: "i9-reference", label: "I-9 document reference", pii: true, required: true }],
    },
    {
      key: "first-day",
      kind: "task",
      title: "First day: welcome, tour and introductions",
      department: "Management",
      deadline: { from: "anchor", days: 0 },
    },
    {
      key: "day-30",
      kind: "task",
      title: "30-day check-in",
      department: "Management",
      deadline: { from: "anchor", days: 30 },
      notBefore: { from: "anchor", days: 25 },
    },
  ],
  edges: [
    ...hard("offer", "accounts", "first-day-plan", "payroll"),
    ...hard("accounts", "equipment"),
    ...hard("first-day-plan", "first-day"),
    ...soft("equipment", "first-day"),
  ],
};

/** The least a departure needs: access off on the day, things back, final pay, benefits. */
export const SIMPLE_OFFBOARDING: Template = {
  id: "library-simple-offboarding",
  name: "Simple offboarding",
  version: 1,
  kind: "offboarding",
  use: "standard",
  basedOn: "starter-offboarding",
  nodes: [
    {
      key: "confirmed",
      kind: "task",
      title: "Last day confirmed",
      department: "HR",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "handover",
      kind: "task",
      title: "Handover notes",
      description: "What they look after, who takes it on, and where the documents are.",
      department: "Management",
      deadline: { from: "anchor", days: -1 },
      notes: true,
    },
    {
      key: "access-off",
      kind: "task",
      title: "Email and accounts switched off",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
    },
    {
      key: "equipment-back",
      kind: "task",
      title: "Laptop and equipment returned",
      department: "IT",
      deadline: { from: "anchor", days: 0 },
      fields: [{ key: "device-serial", label: "Laptop serial number", returns: "device" }],
    },
    {
      key: "keys-back",
      kind: "task",
      title: "Keys and badge returned",
      department: "Management",
      deadline: { from: "anchor", days: 0 },
    },
    {
      key: "final-pay",
      kind: "task",
      title: "Final paycheck",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      finalPay: true,
    },
    {
      key: "benefits",
      kind: "task",
      title: "Benefits ended and COBRA notice sent",
      description: COBRA_DESCRIPTION,
      department: "HR",
      deadline: { from: "anchor", days: 10 },
    },
  ],
  edges: [
    ...hard("confirmed", "handover", "access-off", "equipment-back", "keys-back", "final-pay", "benefits"),
    ...soft("handover", "access-off"),
  ],
};

/* ------------------------------------------------------------ healthcare -- */

const HIPAA_TRAINING =
  "The privacy rule, minimum necessary, the security rule, and how to report a suspected breach. HIPAA requires training for every member of the workforce within a reasonable time of joining (45 CFR 164.530(b)) and a security awareness programme (164.308(a)(5)). Keep the record six years (164.530(j)). Guidance from HHS, not legal advice.";

/** A hospital, clinic, practice or anybody else who handles patients' health information. */
export const HIPAA_ONBOARDING: Template = {
  id: "library-hipaa-onboarding",
  name: "Healthcare onboarding (HIPAA)",
  version: 1,
  kind: "onboarding",
  basedOn: "starter-onboarding",
  questions: [
    {
      key: "phi",
      text: "Will they see or handle patients' health information (PHI)?",
      help: "Anything that identifies a patient together with their care or payment: records, schedules, billing.",
    },
    {
      key: "clinical",
      text: "Is it a clinical role that needs a licence or certification?",
    },
  ],
  nodes: [
    {
      key: "offer",
      kind: "task",
      title: "Offer accepted and start date confirmed",
      department: "HR",
      deadline: { from: "anchor", days: -10 },
    },
    {
      key: "exclusion-check",
      kind: "task",
      title: "Excluded-individuals check",
      description:
        "Search the HHS-OIG List of Excluded Individuals/Entities (LEIE), your state's Medicaid exclusion list and SAM.gov before they start, and monthly after. Paying an excluded person with federal health-programme money can bring civil money penalties. Guidance from HHS-OIG, not legal advice.",
      department: "HR",
      deadline: { from: "anchor", days: -5 },
      gate: true,
      compliance: true,
      fields: [
        {
          key: "result",
          label: "Result",
          options: ["Not listed", "Listed: do not proceed"],
          required: true,
        },
      ],
    },
    {
      key: "background",
      kind: "task",
      title: "Background check cleared",
      department: "HR",
      deadline: { from: "anchor", days: -5 },
      gate: true,
    },
    {
      key: "licence",
      kind: "task",
      title: "Licence or certification verified with the issuing board",
      description: "Check it directly with the board, not from a copy; note when it expires.",
      department: "HR",
      deadline: { from: "anchor", days: -5 },
      gate: true,
      compliance: true,
      conditions: [yes("clinical")],
      fields: [
        { key: "licence-number", label: "Licence number", required: true },
        { key: "licence-expires", label: "Expires" },
      ],
    },
    {
      key: "health-screening",
      kind: "task",
      title: "Health screening and immunisation records on file",
      description: "The TB test and vaccinations your policy and state require for the role.",
      department: "HR",
      deadline: { from: "anchor", days: -1 },
      compliance: true,
      conditions: [yes("clinical")],
      fields: [{ key: "record-reference", label: "Where the record is kept", pii: true }],
    },
    {
      key: "confidentiality",
      kind: "task",
      title: "Confidentiality agreement and sanctions policy signed",
      description:
        "HIPAA requires sanctions for workforce members who break the privacy policies, applied and documented (45 CFR 164.530(e)); they should know them before they see a record.",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
    },
    {
      key: "i9",
      kind: "task",
      title: "I-9 verification completed",
      description: I9_DESCRIPTION,
      department: "HR",
      deadline: { from: "anchor", days: 3, unit: "business" },
      compliance: true,
      fields: [{ key: "i9-reference", label: "I-9 document reference", pii: true, required: true }],
    },
    {
      key: "hipaa-training",
      kind: "task",
      title: "HIPAA privacy and security training",
      description: HIPAA_TRAINING,
      department: "Compliance",
      deadline: { from: "anchor", days: 0 },
      gate: true,
      compliance: true,
    },
    {
      key: "accounts",
      kind: "task",
      title: "Their own login, with multi-factor sign-in",
      description:
        "A login of their own, never a shared one: HIPAA's unique user identification (45 CFR 164.312(a)(2)(i)).",
      department: "IT",
      deadline: { from: "anchor", days: -2 },
    },
    {
      key: "device",
      kind: "task",
      title: "Device issued: encrypted and managed",
      department: "IT",
      deadline: { from: "anchor", days: -1 },
      conditions: [notFor("remote")],
      fields: [{ key: "device-serial", label: "Device serial number", issues: "device" }],
    },
    {
      key: "phi-access",
      kind: "task",
      title: "Health record access granted, by role",
      description:
        "Only what the role needs (the minimum necessary standard, 45 CFR 164.502(b) and 164.514(d)). Name the systems and the level of access.",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
      conditions: [yes("phi")],
      fields: [{ key: "systems", label: "Systems and access level", required: true }],
    },
    {
      key: "badge",
      kind: "task",
      title: "Badge, with any restricted areas",
      description: "Records rooms, pharmacy, server room: only those the role needs.",
      department: "Facilities",
      deadline: { from: "anchor", days: 0 },
    },
    {
      key: "first-day",
      kind: "task",
      title: "First day: welcome, tour and introductions",
      department: "Management",
      deadline: { from: "anchor", days: 0 },
    },
    {
      key: "day-30",
      kind: "task",
      title: "30-day check-in",
      department: "Management",
      deadline: { from: "anchor", days: 30 },
      notBefore: { from: "anchor", days: 25 },
    },
    {
      key: "access-review",
      kind: "task",
      title: "90-day access review: still only what the role needs",
      department: "Compliance",
      deadline: { from: "anchor", days: 90 },
      notBefore: { from: "anchor", days: 85 },
      compliance: true,
      conditions: [yes("phi")],
      inputs: [{ node: "phi-access", field: "systems" }],
    },
  ],
  edges: [
    ...hard(
      "offer",
      "exclusion-check",
      "background",
      "licence",
      "health-screening",
      "accounts",
      "confidentiality",
    ),
    ...hard("accounts", "device", "phi-access"),
    ...hard("exclusion-check", "phi-access", "badge"),
    ...hard("background", "phi-access", "badge"),
    ...hard("licence", "phi-access"),
    ...hard("confidentiality", "hipaa-training"),
    ...hard("hipaa-training", "phi-access"),
    ...soft("health-screening", "first-day"),
    ...hard("phi-access", "access-review"),
  ],
};

/** Somebody who could reach patients' records leaves: that access goes first, and is checked after. */
export const HIPAA_OFFBOARDING: Template = {
  id: "library-hipaa-offboarding",
  name: "Healthcare offboarding (HIPAA)",
  version: 1,
  kind: "offboarding",
  use: "standard",
  basedOn: "starter-offboarding",
  questions: [{ key: "phi", text: "Did they have access to patients' health information (PHI)?" }],
  nodes: [
    {
      key: "confirmed",
      kind: "task",
      title: "Departure confirmed",
      department: "HR",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-it",
      kind: "notify",
      title: "IT: an employee is leaving",
      department: "IT",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-facilities",
      kind: "notify",
      title: "Facilities: an employee is leaving",
      department: "Facilities",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "handover",
      kind: "task",
      title: "Handover notes",
      department: "Management",
      deadline: { from: "anchor", days: -1 },
      notes: true,
    },
    {
      key: "phi-access-off",
      kind: "task",
      title: "Health record access removed",
      description:
        "HIPAA's termination procedures: end access to electronic health information when employment ends (45 CFR 164.308(a)(3)(ii)(C)).",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
      compliance: true,
      conditions: [yes("phi")],
    },
    {
      key: "accounts-off",
      kind: "task",
      title: "Email and other accounts switched off",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
    },
    {
      key: "shared-secrets",
      kind: "task",
      title: "Shared passwords and door codes they knew changed",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
    },
    {
      key: "devices",
      kind: "task",
      title: "Devices returned and checked for patient data",
      description:
        "Anything that held health information is wiped or destroyed before it is reused (45 CFR 164.310(d)(2)).",
      department: "IT",
      deadline: { from: "anchor", days: 0 },
      fields: [{ key: "device-serial", label: "Device serial number", returns: "device" }],
    },
    {
      key: "physical",
      kind: "task",
      title: "Badge, keys and any paper records returned",
      department: "Facilities",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
    },
    {
      key: "confidentiality-reminder",
      kind: "task",
      title: "Reminded in writing that confidentiality continues",
      department: "Compliance",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
    },
    {
      key: "verified",
      kind: "task",
      title: "Every deactivation verified",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
      timeCritical: true,
    },
    {
      key: "activity-review",
      kind: "task",
      title: "Their recent record access reviewed",
      description:
        "Look at their last weeks in the health record's access log for anything they should not have opened (audit controls, 45 CFR 164.312(b); activity review, 164.308(a)(1)(ii)(D)).",
      department: "Compliance",
      deadline: { from: "anchor", days: 5 },
      compliance: true,
      conditions: [yes("phi")],
    },
    {
      key: "final-pay",
      kind: "task",
      title: "Final paycheck",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      finalPay: true,
    },
    {
      key: "cobra",
      kind: "task",
      title: "COBRA notice to the plan administrator",
      description: COBRA_DESCRIPTION,
      department: "HR",
      deadline: { from: "anchor", days: 10 },
    },
    {
      key: "audit-export",
      kind: "task",
      title: "Offboarding audit log exported and filed",
      department: "Compliance",
      deadline: { from: "anchor", days: 5 },
      compliance: true,
    },
  ],
  edges: [
    ...hard(
      "confirmed",
      "tell-it",
      "tell-facilities",
      "handover",
      "confidentiality-reminder",
      "final-pay",
      "cobra",
    ),
    ...hard("tell-it", "phi-access-off", "accounts-off", "devices", "shared-secrets"),
    ...hard("tell-facilities", "physical"),
    ...hard("phi-access-off", "verified"),
    ...hard("accounts-off", "verified"),
    ...hard("physical", "verified"),
    ...hard("devices", "verified"),
    ...hard("verified", "activity-review", "audit-export"),
    ...hard("activity-review", "audit-export"),
    ...soft("handover", "accounts-off"),
  ],
};

/* --------------------------------------------------------------- defense -- */

/** A defense contractor or supplier holding CUI (DFARS 252.204-7012, NIST SP 800-171), and perhaps ITAR data. */
export const DEFENSE_ONBOARDING: Template = {
  id: "library-defense-onboarding",
  name: "Defense contractor onboarding (CUI / ITAR)",
  version: 1,
  kind: "onboarding",
  basedOn: "starter-onboarding",
  questions: [
    {
      key: "cui",
      text: "Will they handle Controlled Unclassified Information (CUI)?",
      help: "Contract documents, drawings, specifications or reports marked CUI.",
    },
    {
      key: "itar",
      text: "Will they see ITAR-controlled technical data?",
      help: "Export-controlled defense articles or technical data: CAD files, build instructions.",
    },
    { key: "remote", text: "Will they connect from outside the office (VPN or remote desktop)?" },
    { key: "privileged", text: "Will they have administrator (privileged) access?" },
  ],
  nodes: [
    {
      key: "offer",
      kind: "task",
      title: "Offer accepted and start date confirmed",
      department: "HR",
      deadline: { from: "anchor", days: -10 },
    },
    {
      key: "access-request",
      kind: "task",
      title: "Access needed, described",
      description:
        "Name the actual applications, groups, shared folders, sites and systems, and the permission on each (read-only, edit). Anything not named is not granted.",
      department: "Management",
      deadline: { from: "anchor", days: -7 },
      fields: [{ key: "systems", label: "Systems, groups and permissions", required: true }],
    },
    {
      key: "tell-it-provider",
      kind: "notify",
      title: "IT provider: a new account is wanted",
      description:
        "If a managed IT provider opens your accounts, put their request address on this card and they are mailed when it is their turn.",
      department: "IT",
      deadline: { from: "anchor", days: -5 },
    },
    {
      key: "us-person",
      kind: "task",
      title: "U.S. person status verified",
      description:
        "Under the ITAR a U.S. person is a citizen, a lawful permanent resident, or somebody granted asylum or refugee status (22 CFR 120.62). Anybody else needs authorisation from the State Department's DDTC, such as an export licence, before they see any technical data. Keep the evidence with HR. Guidance, not legal advice.",
      department: "HR",
      deadline: { from: "anchor", days: -5 },
      gate: true,
      compliance: true,
      conditions: [yes("itar")],
      fields: [
        {
          key: "status",
          label: "Status",
          options: [
            "U.S. citizen",
            "Lawful permanent resident",
            "Asylee or refugee",
            "Foreign person: authorisation needed",
          ],
          required: true,
        },
      ],
    },
    {
      key: "screening",
      kind: "task",
      title: "Restricted-party screening",
      description:
        "Each list searched and the date recorded. The government's Consolidated Screening List (trade.gov) searches most of them at once; SAM.gov has its own.",
      department: "HR",
      deadline: { from: "anchor", days: -5 },
      gate: true,
      compliance: true,
      subtasks: [
        "DDTC debarred parties",
        "OFAC Specially Designated Nationals",
        "BIS Denied Persons List",
        "BIS Entity List",
        "SAM.gov exclusions",
      ],
      fields: [{ key: "evidence", label: "Where the screening record is kept" }],
    },
    {
      key: "background",
      kind: "task",
      title: "Background check cleared",
      description:
        "NIST SP 800-171 3.9.1: screen people before they are given access to systems holding CUI.",
      department: "HR",
      deadline: { from: "anchor", days: -5 },
      gate: true,
      compliance: true,
    },
    {
      key: "i9",
      kind: "task",
      title: "I-9 verification completed",
      description: I9_DESCRIPTION,
      department: "HR",
      deadline: { from: "anchor", days: 3, unit: "business" },
      compliance: true,
      fields: [{ key: "i9-reference", label: "I-9 document reference", pii: true, required: true }],
    },
    {
      key: "policies",
      kind: "task",
      title: "Non-disclosure agreement and security policies signed",
      description: "Each one provided, signed and filed.",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
      subtasks: [
        "Non-disclosure agreement",
        "Acceptable use and cybersecurity",
        "CUI handling and marking",
        "Data classification",
        "Media and data destruction",
        "Physical security",
      ],
    },
    {
      key: "remote-policy",
      kind: "task",
      title: "Remote work and mobile device policy signed",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
      conditions: [yes("remote")],
    },
    {
      key: "privileged-policy",
      kind: "task",
      title: "Privileged user agreement signed",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
      conditions: [yes("privileged")],
    },
    {
      key: "cui-training",
      kind: "task",
      title: "CUI training",
      description:
        "How CUI is marked, stored, shared and destroyed, and how to report an incident (NIST SP 800-171 3.2.1 and 3.2.2). Record the date and the course.",
      department: "Security",
      deadline: { from: "anchor", days: 0 },
      gate: true,
      compliance: true,
      conditions: [yes("cui")],
    },
    {
      key: "itar-training",
      kind: "task",
      title: "Export control (ITAR) training",
      description:
        "What counts as an export (including showing data to a foreign person here), the technology control plan, and who to ask. Record the date and the course.",
      department: "Security",
      deadline: { from: "anchor", days: 0 },
      gate: true,
      compliance: true,
      conditions: [yes("itar")],
    },
    {
      key: "awareness",
      kind: "task",
      title: "Security awareness and insider-threat training",
      description: "NIST SP 800-171 3.2.1 and 3.2.3.",
      department: "Security",
      deadline: { from: "anchor", days: 5 },
      compliance: true,
    },
    {
      key: "accounts",
      kind: "task",
      title: "Account created, with multi-factor sign-in",
      description: "Multi-factor for every sign-in to the network (NIST SP 800-171 3.5.3).",
      department: "IT",
      deadline: { from: "anchor", days: -2 },
    },
    {
      key: "device",
      kind: "task",
      title: "Device issued: encrypted, managed, labelled if it will hold CUI",
      department: "IT",
      deadline: { from: "anchor", days: -1 },
      fields: [{ key: "device-serial", label: "Device serial number", issues: "device" }],
    },
    {
      key: "everyday-access",
      kind: "task",
      title: "Everyday access granted: email, groups, shared folders",
      department: "IT",
      deadline: { from: "anchor", days: 0 },
      inputs: [{ node: "access-request", field: "systems" }],
    },
    {
      key: "cui-access",
      kind: "task",
      title: "CUI access granted",
      description:
        "Only the CUI systems and folders named in the request, after the checks and training before it.",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
      conditions: [yes("cui")],
      inputs: [{ node: "access-request", field: "systems" }],
    },
    {
      key: "itar-access",
      kind: "task",
      title: "ITAR technical data access granted",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
      conditions: [yes("itar")],
      inputs: [{ node: "access-request", field: "systems" }],
    },
    {
      key: "remote-access",
      kind: "task",
      title: "VPN or remote desktop access set up",
      description: "Under a change ticket, if your IT provider works that way.",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
      conditions: [yes("remote")],
    },
    {
      key: "privileged-access",
      kind: "task",
      title: "Administrator access granted, under a change ticket",
      description: "A separate administrator account from their everyday one (NIST SP 800-171 3.1.6).",
      department: "IT",
      deadline: { from: "anchor", days: 5 },
      conditions: [yes("privileged")],
      fields: [{ key: "ticket", label: "Change ticket" }],
    },
    {
      key: "badge",
      kind: "task",
      title: "Badge and area access",
      department: "Facilities",
      deadline: { from: "anchor", days: 0 },
    },
    {
      key: "safety",
      kind: "task",
      title: "Workplace safety orientation",
      department: "Safety",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
      subtasks: [
        "Workplace safety basics",
        "Portable fire extinguishers (29 CFR 1910.157)",
        "Emergency action plan (29 CFR 1910.38)",
      ],
    },
    {
      key: "day-30",
      kind: "task",
      title: "30-day check-in",
      department: "Management",
      deadline: { from: "anchor", days: 30 },
      notBefore: { from: "anchor", days: 25 },
    },
  ],
  edges: [
    ...hard("offer", "access-request", "us-person", "screening", "background", "policies"),
    ...hard("access-request", "tell-it-provider"),
    ...hard("tell-it-provider", "accounts"),
    ...hard(
      "accounts",
      "device",
      "everyday-access",
      "cui-access",
      "itar-access",
      "remote-access",
      "privileged-access",
    ),
    ...hard("screening", "cui-access", "itar-access", "badge"),
    ...hard("background", "cui-access", "itar-access", "badge"),
    ...hard("us-person", "itar-access"),
    ...hard("policies", "everyday-access", "cui-training", "itar-training"),
    ...hard("cui-training", "cui-access"),
    ...hard("itar-training", "itar-access"),
    ...hard("remote-policy", "remote-access"),
    ...hard("privileged-policy", "privileged-access"),
  ],
};

/** A defense contractor's departure: CUI and ITAR access first, devices sanitized, the obligations restated. */
export const DEFENSE_OFFBOARDING: Template = {
  id: "library-defense-offboarding",
  name: "Defense contractor offboarding (CUI / ITAR)",
  version: 1,
  kind: "offboarding",
  use: "standard",
  basedOn: "starter-offboarding",
  questions: [
    { key: "cui", text: "Did they have access to CUI or ITAR-controlled data?" },
    { key: "privileged", text: "Did they have administrator (privileged) access?" },
  ],
  nodes: [
    {
      key: "confirmed",
      kind: "task",
      title: "Departure confirmed and the time access goes agreed",
      department: "Management",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-it",
      kind: "notify",
      title: "IT: an employee is leaving",
      department: "IT",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-it-provider",
      kind: "notify",
      title: "IT provider: an account is to be removed",
      description:
        "If a managed IT provider removes your accounts, put their request address on this card and they are mailed when it is their turn.",
      department: "IT",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-facilities",
      kind: "notify",
      title: "Facilities: an employee is leaving",
      department: "Facilities",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-security",
      kind: "notify",
      title: "Security: an employee with controlled access is leaving",
      department: "Security",
      deadline: { from: "trigger", days: 0 },
      conditions: [yes("cui")],
    },
    {
      key: "accounts-off",
      kind: "task",
      title: "Sign-ins disabled and multi-factor methods removed",
      description:
        "NIST SP 800-171 3.9.2: protect CUI systems during and after a departure. Record the time it was done.",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
      subtasks: [
        "Directory sign-in disabled",
        "Other logins, admin and service accounts checked",
        "Email and collaboration profile removed",
        "Multi-factor methods removed",
        "Password manager access removed",
        "Cloud backup of the account handled",
        "Phone extension deactivated",
      ],
    },
    {
      key: "privileged-off",
      kind: "task",
      title: "Administrator access removed, under a change ticket",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
      conditions: [yes("privileged")],
      fields: [{ key: "ticket", label: "Change ticket", required: true }],
    },
    {
      key: "groups-off",
      kind: "task",
      title: "Removed from CUI, ITAR and security groups",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
      conditions: [yes("cui")],
    },
    {
      key: "devices-back",
      kind: "task",
      title: "Laptop, phone and peripherals returned",
      department: "IT",
      deadline: { from: "anchor", days: 0 },
      fields: [{ key: "device-serial", label: "Laptop serial number", returns: "device" }],
    },
    {
      key: "sanitize",
      kind: "task",
      title: "Devices backed up as needed, then sanitized",
      description:
        "Sanitize to NIST SP 800-88 before reuse, disposal or return (NIST SP 800-171 3.8.3), and keep the evidence.",
      department: "IT",
      deadline: { from: "anchor", days: 5 },
      compliance: true,
      subtasks: [
        "Data backed up where the manager asked for it",
        "Removed from device management and remote tools",
        "Sanitized",
        "CUI labels removed",
        "Asset inventory updated",
      ],
      fields: [{ key: "evidence", label: "Sanitization record" }],
    },
    {
      key: "physical",
      kind: "task",
      title: "Badge, keys, access card and alarm code returned or cancelled",
      department: "Facilities",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      revokesAccess: true,
      timeCritical: true,
      fields: [{ key: "keys", label: "Keys returned (of how many)" }],
    },
    {
      key: "debrief",
      kind: "task",
      title: "Security debrief: continuing obligations acknowledged",
      description:
        "CUI and export-controlled data stay protected after they leave; nothing goes with them. A signed acknowledgement.",
      department: "Security",
      deadline: { from: "anchor", days: 0 },
      compliance: true,
      conditions: [yes("cui")],
    },
    {
      key: "verified",
      kind: "task",
      title: "Removal verified against the time asked for",
      department: "Security",
      deadline: { from: "anchor", days: 1 },
      timeCritical: true,
    },
    {
      key: "final-pay",
      kind: "task",
      title: "Final paycheck",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
      finalPay: true,
    },
    {
      key: "separation-notices",
      kind: "task",
      title: "State separation notices given",
      description:
        "Some states require written notice of the separation and unemployment insurance information by the last day. Mark this not applicable where yours does not.",
      department: "HR",
      deadline: { from: "anchor", days: 0 },
    },
    {
      key: "benefits",
      kind: "task",
      title: "Benefits ended",
      department: "HR",
      deadline: { from: "anchor", days: 5 },
      subtasks: ["Retirement plan", "Health coverage", "Vehicle insurance, if they were on it"],
    },
    {
      key: "cobra",
      kind: "task",
      title: "COBRA notice to the plan administrator",
      description: COBRA_DESCRIPTION,
      department: "HR",
      deadline: { from: "anchor", days: 10 },
    },
    {
      key: "followup-audit",
      kind: "task",
      title: "Follow-up access audit: removed access stays removed",
      department: "Security",
      deadline: { from: "anchor", days: 30 },
      compliance: true,
    },
    {
      key: "audit-export",
      kind: "task",
      title: "Offboarding audit log exported and filed",
      department: "HR",
      deadline: { from: "anchor", days: 5 },
      compliance: true,
    },
  ],
  edges: [
    ...hard(
      "confirmed",
      "tell-it",
      "tell-it-provider",
      "tell-facilities",
      "tell-security",
      "final-pay",
      "separation-notices",
      "benefits",
    ),
    ...hard("tell-it", "accounts-off", "privileged-off", "groups-off", "devices-back"),
    ...hard("tell-it-provider", "accounts-off"),
    ...hard("devices-back", "sanitize"),
    ...hard("tell-facilities", "physical"),
    ...hard("tell-security", "debrief"),
    ...hard("accounts-off", "verified"),
    ...hard("privileged-off", "verified"),
    ...hard("groups-off", "verified"),
    ...hard("physical", "verified"),
    ...hard("devices-back", "verified"),
    ...hard("verified", "followup-audit", "audit-export"),
    ...hard("benefits", "cobra"),
    ...hard("final-pay", "audit-export"),
  ],
};

/* --------------------------------------------------------------- the list -- */

export const TEMPLATE_GROUPS = {
  everyone: "For any organisation",
  healthcare: "Healthcare (HIPAA)",
  defense: "Defense contractors (CUI / ITAR)",
} as const;

export type TemplateGroup = keyof typeof TEMPLATE_GROUPS;

export interface LibraryEntry {
  template: Template;
  group: TemplateGroup;
  /** A sentence on who it is for. */
  summary: string;
  /** Offered when work is started, before anybody adopts anything: the four starters. */
  offeredByDefault: boolean;
}

export const TEMPLATE_LIBRARY: readonly LibraryEntry[] = [
  {
    template: SIMPLE_ONBOARDING,
    group: "everyone",
    summary: "Eight cards for a small team: accounts, a laptop, payroll forms, the I-9 and a first day.",
    offeredByDefault: false,
  },
  {
    template: STANDARD_ONBOARDING,
    group: "everyone",
    summary: "Every department's part for an office, warehouse, remote or executive hire, branching by role.",
    offeredByDefault: true,
  },
  {
    template: SIMPLE_OFFBOARDING,
    group: "everyone",
    summary: "Access off on the last day, things back, final pay and benefits.",
    offeredByDefault: false,
  },
  {
    template: STANDARD_OFFBOARDING,
    group: "everyone",
    summary:
      "Knowledge transfer, equipment, access, final pay, benefits and an exit interview, by reason for leaving.",
    offeredByDefault: true,
  },
  {
    template: INVOLUNTARY_OFFBOARDING,
    group: "everyone",
    summary: "A termination: access gone within thirty minutes of starting, final pay when the law says.",
    offeredByDefault: true,
  },
  {
    template: ACCESS_CHANGE_TEMPLATE,
    group: "everyone",
    summary: "Somebody stays and some of their access goes.",
    offeredByDefault: true,
  },
  {
    template: HIPAA_ONBOARDING,
    group: "healthcare",
    summary:
      "Excluded-individuals and licence checks, HIPAA training and a confidentiality agreement before any health record access, granted by role.",
    offeredByDefault: false,
  },
  {
    template: HIPAA_OFFBOARDING,
    group: "healthcare",
    summary:
      "Health record access removed on the day, devices checked for patient data, and their recent access reviewed.",
    offeredByDefault: false,
  },
  {
    template: DEFENSE_ONBOARDING,
    group: "defense",
    summary:
      "U.S. person and restricted-party screening, CUI and ITAR training as gates before controlled access, policies signed, multi-factor accounts.",
    offeredByDefault: false,
  },
  {
    template: DEFENSE_OFFBOARDING,
    group: "defense",
    summary:
      "Sign-ins and controlled groups removed at the agreed time, devices sanitized, a security debrief, a 30-day audit.",
    offeredByDefault: false,
  },
];

/** Any template utuwrk ships, by id. */
export const libraryTemplate = (id: string): Template | undefined =>
  TEMPLATE_LIBRARY.find((entry) => entry.template.id === id)?.template;

/** What the Templates screen says about one of utuwrk's own, or null for the organisation's. */
export function libraryEntry(
  id: string,
): { group: TemplateGroup; summary: string; offeredByDefault: boolean } | null {
  const entry = TEMPLATE_LIBRARY.find((candidate) => candidate.template.id === id);
  return entry
    ? { group: entry.group, summary: entry.summary, offeredByDefault: entry.offeredByDefault }
    : null;
}
